import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  formatEur,
  formatEurPrecise,
  formatInt,
  formatMonth,
  modelLabel,
  providerLabel,
} from '../lib/format';
import { Card, ErrorText } from './ui';
import { Select } from './Select';

/** The app's "Defalcare pe model", for one month, as the instance last reported it. */
export function UsageTab({ tenantId }: { tenantId: string }) {
  const [month, setMonth] = useState<string | undefined>(undefined);
  const usage = useQuery({
    queryKey: ['tenant-ai-usage', tenantId, month ?? 'latest'],
    queryFn: () => api.tenantAiUsage(tenantId, month),
  });
  const data = usage.data;

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-white">Defalcare pe model</h2>
          {data && (
            <p className="mt-1 text-xs text-slate-400">
              {formatEur(data.totals.costEur)} · {formatInt(data.totals.callCount)} apeluri AI
            </p>
          )}
        </div>
        {data && data.months.length > 0 && (
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400">
            Luna
            <Select
              label="Luna"
              value={data.month}
              onChange={setMonth}
              options={(data.months.includes(data.month)
                ? data.months
                : [data.month, ...data.months]
              ).map((m) => ({ value: m, label: formatMonth(m) }))}
              className="w-48 normal-case tracking-normal"
            />
          </div>
        )}
      </div>

      <ErrorText error={usage.error} />

      <table className="mt-3 w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="py-2">Model</th>
            <th className="py-2">Furnizor</th>
            <th className="py-2 text-right">Apeluri</th>
            <th className="py-2 text-right">Tokeni intrare</th>
            <th className="py-2 text-right">Tokeni ieșire</th>
            <th className="py-2 text-right">Cost (EUR)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800/70">
          {(data?.rows ?? []).map((r) => (
            <tr key={`${r.provider}/${r.model}`}>
              <td className="py-2 text-white" title={r.model}>
                {modelLabel(r.model)}
              </td>
              <td className="py-2 text-slate-300">{providerLabel(r.provider)}</td>
              <td className="py-2 text-right">{formatInt(r.callCount)}</td>
              <td className="py-2 text-right">{formatInt(r.inputTokens)}</td>
              <td className="py-2 text-right">{formatInt(r.outputTokens)}</td>
              <td className="py-2 text-right">{formatEurPrecise(r.costEur)}</td>
            </tr>
          ))}
          {data && data.rows.length === 0 && (
            <tr>
              <td colSpan={6} className="py-3 text-slate-400">
                {data.months.length === 0
                  ? 'Instanța nu a trimis încă defalcarea pe model (versiune mai veche sau niciun apel AI).'
                  : 'Niciun consum AI în luna aleasă.'}
              </td>
            </tr>
          )}
          {usage.isLoading && (
            <tr>
              <td colSpan={6} className="py-3 text-slate-400">
                Se încarcă…
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Card>
  );
}
